import { useEffect, useState } from "react";
import { getLegalMeta, type LegalMeta } from "../../Services/AuthService";
import Footer from "../../Components/Footer";
import "../../Styles/Legal.css";

export type LegalSection = {
  id: string;
  heading: string;

  /** Kort sammanfattning av vad avsnittet ska säga. Ersätts av juridisk text. */
  summary: string;

  /** Punkterna texten måste täcka. Skriven för den som ska fylla i den. */
  points: string[];
};

/**
 * Gemensam ram för /terms och /privacy.
 *
 * Sektionerna är medvetet strukturerade platshållare och inte tom sida
 * eller lorem ipsum. Rubrikerna och punkterna är den disposition texten ska
 * följa, så att den som skriver den juridiska texten vet vad varje avsnitt
 * ska svara på — och så att en läsare redan nu ser vad vi tänker reglera.
 *
 * PLATSHÅLLARSTATUSEN SYNS FÖR LÄSAREN. En sida som ser färdig ut men inte
 * är det är värre än en som säger att den inte är det: den första kan
 * uppfattas som ett bindande avtal.
 *
 * Versionen hämtas från API:et (api/legal.js), samma källa som
 * registreringen skickar tillbaka.
 */
export default function LegalPage({
  title,
  intro,
  sections,
  versionKey,
}: {
  title: string;
  intro: string;
  sections: LegalSection[];
  versionKey: "terms" | "privacy";
}) {
  const [meta, setMeta] = useState<LegalMeta | null>(null);

  useEffect(() => {
    let cancelled = false;

    getLegalMeta()
      .then((value) => {
        if (!cancelled) setMeta(value);
      })
      .catch(() => {
        // Sidan ska gå att läsa även när API:et är nere. Utan versionen
        // saknas bara datumraden.
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const version = versionKey === "terms" ? meta?.termsVersion : meta?.privacyVersion;
  const updatedAt = versionKey === "terms" ? meta?.termsUpdatedAt : meta?.privacyUpdatedAt;

  return (
    <>
      <main className="legal-page">
        <header className="legal-header">
          <h1>{title}</h1>

          {version && (
            <p className="legal-version">
              Version {version} · Last updated {updatedAt}
            </p>
          )}

          <p>{intro}</p>

          <p className="legal-draft" role="note">
            <strong>Draft.</strong> The sections below set out what this document
            will cover. The binding legal text has not been published yet.
          </p>
        </header>

        <nav className="legal-toc" aria-label="Sections">
          <ol>
            {sections.map((section) => (
              <li key={section.id}>
                <a href={`#${section.id}`}>{section.heading}</a>
              </li>
            ))}
          </ol>
        </nav>

        {sections.map((section, index) => (
          <section key={section.id} id={section.id} className="legal-section">
            <h2>
              {index + 1}. {section.heading}
            </h2>

            <p>{section.summary}</p>

            <ul>
              {section.points.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
          </section>
        ))}
      </main>

      <Footer />
    </>
  );
}
